import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import styled from "styled-components";
import rubiksCubeModel from "./assets/RubiksCube.glb";

const CanvasContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  @media (max-width: 768px) {
    pointer-events: none; /* Désactive les interactions sur mobile pour permettre le scroll */
  }
`;

/* ── Config ── */
const STEP = 0.32;
const TOL = 0.15;

/* ── Easing ── */
const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const easeOutBack = (t) => {
  const s = 1.70158;
  return 1 + (s + 1) * Math.pow(t - 1, 3) + s * Math.pow(t - 1, 2);
};

const snap = (v) => Math.round(v / STEP) * STEP;

/* ── Util : attendre N ms ── */
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/* ── Util : animer avec requestAnimationFrame ── */
const animate = (duration, onTick) =>
  new Promise((resolve) => {
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      onTick(p);
      if (p < 1) requestAnimationFrame(tick);
      else resolve();
    };
    requestAnimationFrame(tick);
  });

/* ── Mélange (Three.js Y-up) ── */
const SCRAMBLE = [
  { axis: "y", layer: STEP, angle: Math.PI / 2 },
  { axis: "x", layer: STEP, angle: -Math.PI / 2 },
  { axis: "z", layer: -STEP, angle: Math.PI / 2 },
  { axis: "y", layer: STEP, angle: -Math.PI / 2 },
  { axis: "x", layer: -STEP, angle: Math.PI / 2 },
];

/* ── Résolution = inverse ── */
const SOLVE = [...SCRAMBLE].reverse().map((m) => ({ ...m, angle: -m.angle }));

/* ── Composant ── */
const RubiksCube3D = ({ onOrangeCubePosition, onAnimationComplete }) => {
  const containerRef = useRef(null);
  const aliveRef = useRef(true);
  const orangeCubeRef = useRef(null);
  const orangeMeshRef = useRef(null);
  const cameraRef = useRef(null);
  const animationCompleteRef = useRef(false);

  useEffect(() => {
    aliveRef.current = true;
    const el = containerRef.current;
    if (!el) return;

    /* ── Setup Three.js ── */
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;

    const scene = new THREE.Scene();

    // Environment subtil pour reflets doux sur l'or
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();
    const envScene = new THREE.Scene();
    envScene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const envTex = pmremGenerator.fromScene(envScene, 0.1).texture;
    scene.environment = envTex;

    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    camera.position.set(1.5, 1.5, 3);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Éclairage doux et uniforme (pour matériaux mats)
    scene.add(new THREE.AmbientLight(0xffffff, 1.2));

    // Key light douce
    const keyLight = new THREE.DirectionalLight(0xfff8f0, 1.2);
    keyLight.position.set(4, 6, 5);
    scene.add(keyLight);

    // Fill light
    const fillLight = new THREE.DirectionalLight(0xf0f0ff, 0.6);
    fillLight.position.set(-5, 3, -3);
    scene.add(fillLight);

    // Bottom fill (éviter les ombres trop noires en bas)
    const bottomLight = new THREE.DirectionalLight(0xffffff, 0.3);
    bottomLight.position.set(0, -5, 2);
    scene.add(bottomLight);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableZoom = false;
    controls.enablePan = false;
    controls.autoRotate = false;

    const resize = () => {
      const { width, height } = el.getBoundingClientRect();
      const w = width || 500;
      const h = height || 500;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    el.appendChild(renderer.domElement);
    window.addEventListener("resize", resize);

    /* ── Render loop ── */
    let fid;
    const renderLoop = () => {
      fid = requestAnimationFrame(renderLoop);
      controls.update();

      // Mettre à jour la position du cube orange (seulement pendant l'animation)
      if (!animationCompleteRef.current && orangeCubeRef.current && cameraRef.current && onOrangeCubePosition) {
        // Obtenir la position mondiale du cube parent (centre du cube)
        const worldPos = new THREE.Vector3();
        orangeCubeRef.current.getWorldPosition(worldPos);

        // Projeter en coordonnées écran
        const screenPos = worldPos.clone();
        screenPos.project(cameraRef.current);

        // Convertir en coordonnées viewport
        const { width, height } = el.getBoundingClientRect();
        const rect = el.getBoundingClientRect();

        const x = rect.left + (screenPos.x * 0.5 + 0.5) * width;
        const y = rect.top + (-screenPos.y * 0.5 + 0.5) * height;

        onOrangeCubePosition({ x, y });
      }

      renderer.render(scene, camera);
    };
    renderLoop();

    /* ── Charger et animer ── */
    new GLTFLoader().load(rubiksCubeModel, async (gltf) => {
      const model = gltf.scene;

      // ── 1. CACHER TOUT AVANT d'ajouter à la scène ──
      model.visible = false;
      scene.add(model);

      // ── 2. Collecter les cubies ──
      const cubies = [];
      let cubeRoot = null;

      model.traverse((node) => {
        if (
          node.name &&
          node.name.startsWith("Rubik_") &&
          node.children.length > 0 &&
          !node.name.endsWith("_mesh")
        ) {
          cubies.push(node);
          if (!cubeRoot) cubeRoot = node.parent;

          // Identifier le cube orange par son nom
          if (node.name === "Rubik_1_-1_1") {
            node.traverse((child) => {
              if (child.isMesh && child.material) {
                if (!orangeCubeRef.current) {
                  orangeCubeRef.current = node;
                  orangeMeshRef.current = child;
                  console.log("🟧 Cube orange trouvé:", node.name);
                }
              }
            });
          }
        }
      });

      // Fallback mesh
      if (cubies.length === 0) {
        model.traverse((node) => {
          if (node.isMesh && node.name.startsWith("Rubik_")) {
            cubies.push(node);
            if (!cubeRoot) cubeRoot = node.parent;
          }
        });
      }
      if (!cubeRoot) cubeRoot = model;

      console.log(`✅ ${cubies.length} cubies`);

      // DEBUG: Lister TOUS les matériaux uniques
      const allMaterials = [];
      model.traverse((child) => {
        if (child.isMesh && child.material && child.material.name) {
          allMaterials.push(child.material.name);
        }
      });
      const uniqueMats = [...new Set(allMaterials)];
      console.log("📋 Tous les matériaux trouvés:", uniqueMats);

      console.log(`✅ ${cubies.length} cubies`);

      // ── 3. Sauvegarder positions cibles et cacher chaque cubie ──
      const targetPos = new Map();
      cubies.forEach((c) => {
        targetPos.set(c, c.position.clone());
        c.position.set(0, 0, 0);
        c.scale.set(0.001, 0.001, 0.001); // quasi invisible
      });

      // Identifier le cube central
      const centerCube = cubies.find((c) => {
        const t = targetPos.get(c);
        return (
          Math.abs(t.x) < TOL && Math.abs(t.y) < TOL && Math.abs(t.z) < TOL
        );
      });

      // Trier les autres par distance au centre (proches en premier)
      const others = cubies
        .filter((c) => c !== centerCube)
        .sort((a, b) => {
          const ta = targetPos.get(a);
          const tb = targetPos.get(b);
          return ta.length() - tb.length();
        });

      // Maintenant on peut afficher le modèle (tout est caché par scale 0.001)
      model.visible = true;

      // ── Helper fonctions ──
      const getLayer = (axis, value) =>
        cubies.filter((c) => Math.abs(c.position[axis] - value) < TOL);

      const applyMoveInstant = (move) => {
        const layer = getLayer(move.axis, move.layer);
        if (layer.length === 0) return;

        const pivot = new THREE.Group();
        cubeRoot.add(pivot);
        layer.forEach((c) => pivot.attach(c));

        pivot.rotation[move.axis] = move.angle;
        pivot.updateMatrixWorld(true);

        layer.forEach((c) => {
          cubeRoot.attach(c);
          c.position.x = snap(c.position.x);
          c.position.y = snap(c.position.y);
          c.position.z = snap(c.position.z);
        });

        cubeRoot.remove(pivot);
      };

      const animateLayerMove = async (move) => {
        if (!aliveRef.current) return;

        const layer = getLayer(move.axis, move.layer);
        if (layer.length === 0) return;

        const pivot = new THREE.Group();
        cubeRoot.add(pivot);
        layer.forEach((c) => pivot.attach(c));

        await animate(600, (p) => {
          const val = move.angle * easeInOutCubic(p);
          pivot.rotation.set(0, 0, 0);
          pivot.rotation[move.axis] = val;
        });

        // Finaliser
        pivot.rotation.set(0, 0, 0);
        pivot.rotation[move.axis] = move.angle;
        pivot.updateMatrixWorld(true);

        layer.forEach((c) => {
          cubeRoot.attach(c);
          c.position.x = snap(c.position.x);
          c.position.y = snap(c.position.y);
          c.position.z = snap(c.position.z);
        });

        cubeRoot.remove(pivot);
      };

      // ══════════════════════════════════════════
      // PHASE A : Mélanger INSTANTANÉMENT (invisible)
      //           puis sauvegarder les positions mélangées
      // ══════════════════════════════════════════
      if (!aliveRef.current) return;
      console.log("🎬 Phase A : scramble silencieux");

      // D'abord remettre tous les cubes à leur position résolue (invisible)
      cubies.forEach((c) => {
        const target = targetPos.get(c);
        c.position.copy(target);
        c.scale.set(0.001, 0.001, 0.001); // toujours invisible
      });

      // Appliquer le mélange instantanément
      for (const move of SCRAMBLE) {
        applyMoveInstant(move);
      }

      // Sauvegarder les positions mélangées
      const scrambledPos = new Map();
      cubies.forEach((c) => {
        scrambledPos.set(c, c.position.clone());
        c.position.set(0, 0, 0); // re-cacher au centre
        c.scale.set(0.001, 0.001, 0.001);
      });

      // ══════════════════════════════════════════
      // PHASE B : Cube central apparaît (0.5s)
      // ══════════════════════════════════════════
      if (!aliveRef.current) return;
      console.log("🎬 Phase B : cube central");

      if (centerCube) {
        await animate(500, (p) => {
          const s = easeOutBack(p);
          centerCube.scale.set(s, s, s);
        });
        centerCube.scale.set(1, 1, 1);
      }

      await wait(300);

      // ══════════════════════════════════════════
      // PHASE C : Déploiement MÉLANGÉ
      //           Les cubes apparaissent directement dans le désordre
      // ══════════════════════════════════════════
      if (!aliveRef.current) return;
      console.log("🎬 Phase C : déploiement mélangé");

      // Calculer la distance de chaque cube (depuis position mélangée)
      const cubeDist = new Map();
      let maxDist = 0;
      others.forEach((c) => {
        const t = scrambledPos.get(c);
        const d = Math.sqrt(t.x * t.x + t.y * t.y + t.z * t.z);
        cubeDist.set(c, d);
        if (d > maxDist) maxDist = d;
      });

      await animate(2000, (p) => {
        others.forEach((c) => {
          const target = scrambledPos.get(c);
          const dist = cubeDist.get(c);
          const ratio = dist / maxDist;

          const delay = ratio * 0.3;
          const localP = Math.max(0, (p - delay) / (1 - delay));

          const overshoot = 1.2 + ratio * 0.6;
          const eased =
            localP < 1
              ? 1 +
                (overshoot + 1) * Math.pow(localP - 1, 3) +
                overshoot * Math.pow(localP - 1, 2)
              : 1;

          const scaleP = Math.max(0, (p - delay * 0.5) / (1 - delay * 0.5));
          const scaleFactor = Math.min(easeOutBack(Math.min(scaleP, 1)), 1);

          c.position.x = target.x * eased;
          c.position.y = target.y * eased;
          c.position.z = target.z * eased;
          c.scale.set(scaleFactor, scaleFactor, scaleFactor);
        });
      });

      // Snap aux positions mélangées
      others.forEach((c) => {
        const target = scrambledPos.get(c);
        c.position.copy(target);
        c.scale.set(1, 1, 1);
      });

      await wait(800);

      // ══════════════════════════════════════════
      // PHASE D : Résolution animée — on remet de l'ordre
      // ══════════════════════════════════════════
      if (!aliveRef.current) return;
      console.log("🧩 Phase D : résolution");

      for (let i = 0; i < SOLVE.length; i++) {
        if (!aliveRef.current) return;
        await animateLayerMove(SOLVE[i]);
        if (i < SOLVE.length - 1) await wait(250);
      }

      console.log("🎉 Terminé ! Cube résolu.");

      // Notifier que l'animation est terminée
      animationCompleteRef.current = true;
      if (onAnimationComplete) {
        onAnimationComplete();
      }

      await wait(500);

      // ══════════════════════════════════════════
      // PHASE E : Idle — cubes qui "respirent"
      //           Un cube random sort légèrement puis revient
      // ══════════════════════════════════════════
      console.log("💫 Phase E : idle breathing");

      const idleCubes = cubies.filter((c) => c !== centerCube);

      const popCube = async (cube) => {
        if (!aliveRef.current) return;
        const target = targetPos.get(cube);
        if (!target) return;

        // Direction: le cube sort vers l'extérieur (depuis le centre)
        const dir = target.clone().normalize();
        const popDist = 0.06 + Math.random() * 0.06; // 0.06 à 0.12

        // Sortie douce
        await animate(400, (p) => {
          const ep = easeInOutCubic(p);
          cube.position.x = target.x + dir.x * popDist * ep;
          cube.position.y = target.y + dir.y * popDist * ep;
          cube.position.z = target.z + dir.z * popDist * ep;
        });

        // Petite pause dehors
        await wait(100 + Math.random() * 200);

        // Retour doux
        await animate(500, (p) => {
          const ep = easeInOutCubic(p);
          cube.position.x = target.x + dir.x * popDist * (1 - ep);
          cube.position.y = target.y + dir.y * popDist * (1 - ep);
          cube.position.z = target.z + dir.z * popDist * (1 - ep);
        });

        // Snap
        cube.position.copy(target);
      };

      while (aliveRef.current) {
        // Attendre entre 1 et 3 secondes
        await wait(1000 + Math.random() * 2000);
        if (!aliveRef.current) return;

        // Choisir 1 à 3 cubes aléatoires
        const count = 1 + Math.floor(Math.random() * 3);
        const shuffled = [...idleCubes].sort(() => Math.random() - 0.5);
        const selected = shuffled.slice(0, count);

        // Les animer en parallèle
        await Promise.all(selected.map((c) => popCube(c)));
      }
    });

    /* ── Cleanup ── */
    return () => {
      aliveRef.current = false;
      cancelAnimationFrame(fid);
      window.removeEventListener("resize", resize);
      controls.dispose();
      pmremGenerator.dispose();
      renderer.dispose();
      scene.clear();
      if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
    };
  }, []);

  return <CanvasContainer ref={containerRef} />;
};

export default RubiksCube3D;
